/**
 * ============================================================
 * KENZU STORY CONTENT
 * Sección editorial multimedia para Shopify 2.0
 * ============================================================
 *
 * Funciones:
 * - Animaciones mediante IntersectionObserver.
 * - Gestión de vídeos alojados en Shopify.
 * - Integración de YouTube y Vimeo.
 * - Botón de reproducción manual.
 * - Pausa automática cuando la sección sale del viewport.
 * - Compatibilidad con el editor visual de Shopify.
 */

(() => {
  "use strict";

  const SECTION_SELECTOR = "[data-kenzu-story]";
  const EXTERNAL_VIDEO_SELECTOR = "[data-story-external-video]";
  const PLAY_BUTTON_SELECTOR = "[data-story-play]";

  const controllers = new WeakMap();

  class KenzuStory {
    /**
     * @param {HTMLElement} section
     */
    constructor(section) {
      this.section = section;

      this.externalVideo = section.querySelector(
        EXTERNAL_VIDEO_SELECTOR
      );

      this.playButton = section.querySelector(
        PLAY_BUTTON_SELECTOR
      );

      this.hostedVideos = Array.from(
        section.querySelectorAll("video")
      );

      this.autoplay =
        section.dataset.autoplay === "true";

      this.pauseOutsideViewport =
        section.dataset.pauseOutside === "true";

      this.reducedMotionQuery = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      );

      this.abortController = new AbortController();

      this.revealObserver = null;
      this.videoObserver = null;
      this.externalVideoObserver = null;

      this.externalIframe = null;
      this.externalVideoLoaded = false;
      this.externalVideoPlaying = false;

      this.handlePlayButtonClick =
        this.handlePlayButtonClick.bind(this);

      this.handleDocumentVisibility =
        this.handleDocumentVisibility.bind(this);

      this.init();
    }

    /**
     * Inicializa todos los comportamientos de la sección.
     */
    init() {
      this.initializeRevealAnimation();
      this.initializeHostedVideos();
      this.initializeHostedVideoObserver();
      this.initializeExternalVideo();

      this.playButton?.addEventListener(
        "click",
        this.handlePlayButtonClick,
        {
          signal: this.abortController.signal
        }
      );

      document.addEventListener(
        "visibilitychange",
        this.handleDocumentVisibility,
        {
          signal: this.abortController.signal
        }
      );
    }

    /**
     * Activa las animaciones cuando la sección entra en pantalla.
     */
    initializeRevealAnimation() {
      if (
        this.reducedMotionQuery.matches ||
        !("IntersectionObserver" in window)
      ) {
        this.section.classList.add("is-visible");
        return;
      }

      this.revealObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            this.section.classList.add("is-visible");
            observer.unobserve(entry.target);
          });
        },
        {
          threshold: 0.12,
          rootMargin: "0px 0px -8% 0px"
        }
      );

      this.revealObserver.observe(this.section);
    }

    /**
     * Prepara los vídeos cargados directamente en Shopify.
     */
    initializeHostedVideos() {
      if (!this.hostedVideos.length) return;

      this.hostedVideos.forEach((video) => {
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "");

        /*
         * Los navegadores requieren vídeo silenciado
         * para permitir reproducción automática.
         */
        if (this.autoplay) {
          video.muted = true;
          video.defaultMuted = true;
        }

        /*
         * Evita descargar todo el archivo inmediatamente.
         */
        if (!video.hasAttribute("preload")) {
          video.preload = "metadata";
        }

        /*
         * Intentamos reproducir solo el vídeo visible
         * según el breakpoint actual.
         */
        if (
          this.autoplay &&
          this.isElementVisible(video) &&
          this.isVideoBreakpointActive(video)
        ) {
          this.safePlay(video);
        }
      });
    }

    /**
     * Pausa y reproduce vídeos Shopify según su visibilidad.
     */
    initializeHostedVideoObserver() {
      if (!this.hostedVideos.length) return;
      if (!this.pauseOutsideViewport) return;
      if (!("IntersectionObserver" in window)) return;

      this.videoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;

            if (!(video instanceof HTMLVideoElement)) {
              return;
            }

            const shouldPlay =
              entry.isIntersecting &&
              this.autoplay &&
              !document.hidden &&
              this.isVideoBreakpointActive(video);

            if (shouldPlay) {
              this.safePlay(video);
            } else {
              video.pause();
            }
          });
        },
        {
          threshold: 0.15,
          rootMargin: "100px 0px 100px 0px"
        }
      );

      this.hostedVideos.forEach((video) => {
        this.videoObserver.observe(video);
      });
    }

    /**
     * Inicializa el vídeo externo.
     *
     * Si autoplay está activo, el iframe se carga cuando la sección
     * se aproxima al viewport. Si está desactivado, se carga al pulsar
     * el botón de reproducción.
     */
    initializeExternalVideo() {
      if (!this.externalVideo) return;

      if (this.autoplay) {
        this.externalVideo.classList.add("is-autoplay");

        if ("IntersectionObserver" in window) {
          this.externalVideoObserver =
            new IntersectionObserver(
              (entries, observer) => {
                entries.forEach((entry) => {
                  if (!entry.isIntersecting) return;

                  this.loadExternalVideo(true);
                  observer.unobserve(entry.target);
                });
              },
              {
                threshold: 0,
                rootMargin: "300px 0px"
              }
            );

          this.externalVideoObserver.observe(
            this.externalVideo
          );
        } else {
          this.loadExternalVideo(true);
        }
      }
    }

    /**
     * Crea el iframe de YouTube o Vimeo.
     *
     * @param {boolean} forceAutoplay
     */
    loadExternalVideo(forceAutoplay = false) {
      if (!this.externalVideo) return;
      if (this.externalVideoLoaded) return;

      const videoType =
        this.externalVideo.dataset.videoType;

      const videoId =
        this.externalVideo.dataset.videoId;

      const loop =
        this.externalVideo.dataset.loop === "true";

      const muted =
        this.externalVideo.dataset.muted === "true";

      const controls =
        this.externalVideo.dataset.controls === "true";

      const autoplay =
        forceAutoplay ||
        this.externalVideo.dataset.autoplay === "true";

      if (!videoType || !videoId) return;

      const iframe = document.createElement("iframe");

      iframe.className =
        "kenzu-story__external-iframe";

      iframe.title = "Vídeo de la historia de Kenzu";

      iframe.allow =
        "autoplay; encrypted-media; fullscreen; picture-in-picture";

      iframe.setAttribute("allowfullscreen", "");
      iframe.setAttribute("frameborder", "0");

      /*
       * En reproducción manual se carga al pulsar,
       * por lo que no necesita lazy loading adicional.
       */
      iframe.loading = autoplay ? "lazy" : "eager";

      if (videoType === "youtube") {
        iframe.src = this.buildYouTubeUrl({
          videoId,
          autoplay,
          loop,
          muted: muted || autoplay,
          controls
        });
      }

      if (videoType === "vimeo") {
        iframe.src = this.buildVimeoUrl({
          videoId,
          autoplay,
          loop,
          muted: muted || autoplay,
          controls
        });
      }

      if (!iframe.src) return;

      iframe.addEventListener(
        "load",
        () => {
          this.externalVideo?.classList.add(
            "is-loaded"
          );

          if (autoplay) {
            this.externalVideoPlaying = true;

            this.externalVideo?.classList.add(
              "is-playing"
            );
          }
        },
        {
          once: true,
          signal: this.abortController.signal
        }
      );

      this.externalVideo.appendChild(iframe);

      this.externalIframe = iframe;
      this.externalVideoLoaded = true;
    }

    /**
     * Genera la URL de YouTube.
     *
     * @param {Object} options
     * @returns {string}
     */
    buildYouTubeUrl({
      videoId,
      autoplay,
      loop,
      muted,
      controls
    }) {
      const params = new URLSearchParams({
        autoplay: autoplay ? "1" : "0",
        mute: muted ? "1" : "0",
        controls: controls ? "1" : "0",
        loop: loop ? "1" : "0",
        playsinline: "1",
        rel: "0",
        modestbranding: "1",
        enablejsapi: "1",
        origin: window.location.origin
      });

      if (loop) {
        params.set("playlist", videoId);
      }

      return `https://www.youtube.com/embed/${encodeURIComponent(
        videoId
      )}?${params.toString()}`;
    }

    /**
     * Genera la URL de Vimeo.
     *
     * @param {Object} options
     * @returns {string}
     */
    buildVimeoUrl({
      videoId,
      autoplay,
      loop,
      muted,
      controls
    }) {
      const params = new URLSearchParams({
        autoplay: autoplay ? "1" : "0",
        muted: muted ? "1" : "0",
        loop: loop ? "1" : "0",
        controls: controls ? "1" : "0",
        autopause: "0",
        playsinline: "1",
        dnt: "1"
      });

      /*
       * El modo background mejora el comportamiento
       * para vídeo decorativo sin controles.
       */
      if (autoplay && !controls) {
        params.set("background", "1");
      }

      return `https://player.vimeo.com/video/${encodeURIComponent(
        videoId
      )}?${params.toString()}`;
    }

    /**
     * Gestiona la reproducción manual del vídeo externo.
     */
    handlePlayButtonClick() {
      if (!this.externalVideo) return;

      this.externalVideo.classList.add("is-playing");
      this.externalVideoPlaying = true;

      if (!this.externalVideoLoaded) {
        this.loadExternalVideo(true);
        return;
      }

      this.playExternalVideo();
    }

    /**
     * Envía el comando de reproducción al iframe.
     */
    playExternalVideo() {
      if (!this.externalIframe) return;
      if (!this.externalVideo) return;

      const videoType =
        this.externalVideo.dataset.videoType;

      if (videoType === "youtube") {
        this.externalIframe.contentWindow?.postMessage(
          JSON.stringify({
            event: "command",
            func: "playVideo",
            args: []
          }),
          "*"
        );
      }

      if (videoType === "vimeo") {
        this.externalIframe.contentWindow?.postMessage(
          {
            method: "play"
          },
          "*"
        );
      }
    }

    /**
     * Pausa el vídeo externo.
     */
    pauseExternalVideo() {
      if (!this.externalIframe) return;
      if (!this.externalVideo) return;

      const videoType =
        this.externalVideo.dataset.videoType;

      if (videoType === "youtube") {
        this.externalIframe.contentWindow?.postMessage(
          JSON.stringify({
            event: "command",
            func: "pauseVideo",
            args: []
          }),
          "*"
        );
      }

      if (videoType === "vimeo") {
        this.externalIframe.contentWindow?.postMessage(
          {
            method: "pause"
          },
          "*"
        );
      }
    }

    /**
     * Reacciona cuando el usuario cambia de pestaña.
     */
    handleDocumentVisibility() {
      if (document.hidden) {
        this.pauseAllVideos();
        return;
      }

      if (
        this.autoplay &&
        this.isElementVisible(this.section)
      ) {
        this.playVisibleHostedVideo();

        if (
          this.externalVideoPlaying &&
          this.externalIframe
        ) {
          this.playExternalVideo();
        }
      }
    }

    /**
     * Reproduce únicamente el vídeo Shopify correspondiente
     * al breakpoint actual.
     */
    playVisibleHostedVideo() {
      this.hostedVideos.forEach((video) => {
        if (
          this.isElementVisible(video) &&
          this.isVideoBreakpointActive(video)
        ) {
          this.safePlay(video);
        } else {
          video.pause();
        }
      });
    }

    /**
     * Pausa todos los vídeos de la sección.
     */
    pauseAllVideos() {
      this.hostedVideos.forEach((video) => {
        video.pause();
      });

      this.pauseExternalVideo();
    }

    /**
     * Ejecuta play() sin generar errores sin capturar.
     *
     * @param {HTMLVideoElement} video
     */
    safePlay(video) {
      if (!(video instanceof HTMLVideoElement)) return;

      const playPromise = video.play();

      if (
        playPromise &&
        typeof playPromise.catch === "function"
      ) {
        playPromise.catch(() => {
          /*
           * Autoplay puede ser bloqueado por el navegador.
           * No interrumpimos la experiencia ni mostramos errores.
           */
        });
      }
    }

    /**
     * Comprueba si el vídeo pertenece al breakpoint activo.
     *
     * @param {HTMLVideoElement} video
     * @returns {boolean}
     */
    isVideoBreakpointActive(video) {
      const isDesktop = window.matchMedia(
        "(min-width: 750px)"
      ).matches;

      if (
        video.classList.contains(
          "kenzu-story__video--desktop"
        )
      ) {
        return isDesktop;
      }

      if (
        video.classList.contains(
          "kenzu-story__video--mobile"
        )
      ) {
        return !isDesktop;
      }

      return true;
    }

    /**
     * Comprueba si un elemento ocupa parte del viewport.
     *
     * @param {Element} element
     * @returns {boolean}
     */
    isElementVisible(element) {
      if (!(element instanceof Element)) return false;

      const rect = element.getBoundingClientRect();

      return (
        rect.bottom > 0 &&
        rect.right > 0 &&
        rect.top < window.innerHeight &&
        rect.left < window.innerWidth
      );
    }

    /**
     * Limpia observadores, vídeos y eventos.
     */
    destroy() {
      this.pauseAllVideos();

      this.abortController.abort();

      this.revealObserver?.disconnect();
      this.videoObserver?.disconnect();
      this.externalVideoObserver?.disconnect();

      this.externalIframe?.remove();

      this.externalIframe = null;
      this.externalVideoLoaded = false;
      this.externalVideoPlaying = false;
    }
  }

  /**
   * Inicializa una sección.
   *
   * @param {HTMLElement} section
   */
  const initializeSection = (section) => {
    if (!(section instanceof HTMLElement)) return;
    if (controllers.has(section)) return;

    const controller = new KenzuStory(section);

    controllers.set(section, controller);
  };

  /**
   * Busca e inicializa secciones dentro de un nodo.
   *
   * @param {Document|HTMLElement} root
   */
  const initializeSections = (root = document) => {
    if (
      root instanceof HTMLElement &&
      root.matches(SECTION_SELECTOR)
    ) {
      initializeSection(root);
    }

    root
      .querySelectorAll?.(SECTION_SELECTOR)
      .forEach(initializeSection);
  };

  /**
   * Destruye secciones antes de que Shopify las retire.
   *
   * @param {HTMLElement} root
   */
  const destroySections = (root) => {
    const sections = [];

    if (
      root instanceof HTMLElement &&
      root.matches(SECTION_SELECTOR)
    ) {
      sections.push(root);
    }

    root
      .querySelectorAll?.(SECTION_SELECTOR)
      .forEach((section) => {
        sections.push(section);
      });

    sections.forEach((section) => {
      controllers.get(section)?.destroy();
      controllers.delete(section);
    });
  };

  /**
   * Arranque inicial.
   */
  const boot = () => {
    initializeSections(document);
  };

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      boot,
      {
        once: true
      }
    );
  } else {
    boot();
  }

  /**
   * Eventos del editor visual de Shopify.
   */
  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeSections(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:unload",
    (event) => {
      destroySections(event.target);
    }
  );

  document.addEventListener(
    "shopify:section:select",
    (event) => {
      const section =
        event.target.matches?.(SECTION_SELECTOR)
          ? event.target
          : event.target.querySelector?.(
              SECTION_SELECTOR
            );

      if (!section) return;

      section.classList.add("is-visible");

      const controller = controllers.get(section);

      controller?.playVisibleHostedVideo();
    }
  );

  document.addEventListener(
    "shopify:block:select",
    (event) => {
      const section =
        event.target.closest?.(SECTION_SELECTOR);

      section?.classList.add("is-visible");
    }
  );
})();